import { useState } from "react";
import { ActivityIndicator, StyleSheet, TextInput, TouchableOpacity, View } from "react-native";
import { Text } from "react-native-paper";

export const SUPPLEMENT_ROUTES = ["Oral", "Sublingual", "Topical", "Feeding tube"];

/** Same pills and Other pattern used by medication dose/frequency questions. */
export function SuggestionQuestion({
    label,
    value,
    onChange,
    options,
    required = false,
    loading = false,
    showError = false,
    placeholder = "Enter other value",
    hint,
    errorMessage
}: {
    label: string;
    value: string;
    onChange: (value: string) => void;
    options: string[];
    required?: boolean;
    loading?: boolean;
    showError?: boolean;
    placeholder?: string;
    hint?: string;
    errorMessage?: string;
}) {
    const [other, setOther] = useState(false);
    const choices = [
        ...new Set(
            [...options, ...(!other && value ? [value] : [])].map((v) => v.trim()).filter(Boolean)
        )
    ];
    return (
        <View style={styles.field}>
            <Text style={styles.label}>
                {label}
                {required ? " *" : " (optional)"}
            </Text>
            {hint && <Text style={styles.hint}>{hint}</Text>}
            {loading && (
                <View style={styles.loading}>
                    <ActivityIndicator size="small" color="#A78BFA" />
                    <Text style={styles.hint}>Loading suggestions…</Text>
                </View>
            )}
            <View style={styles.chips}>
                {choices.map((choice) => (
                    <TouchableOpacity
                        key={choice}
                        accessibilityRole="button"
                        accessibilityLabel={`${label}: ${choice}`}
                        accessibilityState={{ selected: !other && value === choice }}
                        onPress={() => {
                            setOther(false);
                            onChange(choice);
                        }}
                        style={[styles.chip, !other && value === choice && styles.selected]}
                    >
                        <Text
                            style={[
                                styles.chipText,
                                !other && value === choice && styles.selectedText
                            ]}
                        >
                            {choice}
                        </Text>
                    </TouchableOpacity>
                ))}
                <TouchableOpacity
                    accessibilityRole="button"
                    accessibilityLabel={`${label}: Other`}
                    accessibilityState={{ selected: other }}
                    onPress={() => {
                        setOther(true);
                        onChange("");
                    }}
                    style={[styles.chip, styles.other, other && styles.selected]}
                >
                    <Text style={[styles.chipText, other && styles.selectedText]}>Other</Text>
                </TouchableOpacity>
            </View>
            {other && (
                <TextInput
                    accessibilityLabel={label}
                    value={value}
                    onChangeText={onChange}
                    placeholder={placeholder}
                    placeholderTextColor="#B0AECE"
                    style={styles.input}
                    autoCorrect={false}
                    spellCheck={false}
                />
            )}
            {showError && required && !value.trim() && (
                <Text style={styles.error}>
                    {errorMessage ?? `Please select ${label.toLowerCase()}`}
                </Text>
            )}
        </View>
    );
}
const styles = StyleSheet.create({
    field: { marginBottom: 20 },
    label: { fontSize: 15, fontWeight: "700", color: "#1F1547", marginBottom: 8 },
    hint: { fontSize: 12, color: "#6B6B8D", marginBottom: 8 },
    loading: { flexDirection: "row", gap: 8, alignItems: "center" },
    chips: { flexDirection: "row", flexWrap: "wrap", gap: 8 },
    chip: {
        paddingHorizontal: 16,
        paddingVertical: 10,
        borderRadius: 20,
        borderWidth: 2,
        borderColor: "#EDE9FE",
        backgroundColor: "#FFFFFF"
    },
    selected: { borderColor: "#A78BFA", backgroundColor: "#A78BFA" },
    other: { borderStyle: "dashed", borderColor: "#B0AECE" },
    chipText: { fontSize: 13, fontWeight: "600", color: "#6B6B8D" },
    selectedText: { color: "#FFFFFF" },
    input: {
        borderWidth: 2,
        borderColor: "#EDE9FE",
        borderRadius: 12,
        padding: 14,
        fontSize: 15,
        color: "#1F1547",
        backgroundColor: "#FFFFFF",
        marginTop: 12
    },
    error: { fontSize: 11, color: "#EF4444", fontWeight: "600", marginTop: 4 }
});
